<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { FileData } from "~/views/trigger/types";
import {
  PInputFile,
  IconProfileMd as ImageIcon,
  IconFileMd as DocumentIcon,
  IconVideoMd as VideoIcon,
  IconTrashMd as TrashIcon,
  Errors,
  InputFileError
} from "@profeat/ui-kit";
import { convertTo, getInByte } from "~/utils/file";
import { TemplateContentMaxSizeMb, TemplateContentType } from "~/entities/template";

const formatContent = {
  [TemplateContentType.IMAGE]: {
    sizeLimit: Number(getInByte(TemplateContentMaxSizeMb.IMAGE, "MB")),
    formats: ["image/png", "image/jpeg"],
    icon: ImageIcon,
  },
  [TemplateContentType.DOCUMENT]: {
    sizeLimit: Number(getInByte(TemplateContentMaxSizeMb.DOCUMENT, "MB")),
    formats: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    icon: DocumentIcon,
  },
  [TemplateContentType.VIDEO]: {
    sizeLimit: Number(getInByte(TemplateContentMaxSizeMb.VIDEO, "MB")),
    formats: ["video/mp4", "video/3gpp"],
    icon: VideoIcon,
  },
};

const props = defineProps<{
  title: string;
  type: Exclude<TemplateContentType, TemplateContentType.TEXT>;
  value?: FileData | null;
}>();

const emit = defineEmits<{
  valueChanged: [FileData | null];
}>();

const file = ref<FileData | null>(props.value || null);
const isError = ref<Errors.fileSizeLimitExceeded | Errors.fileTypeInvalid | null>(null);
const fileSize = ref<number | null>(null);

const withPreview = computed(() => {
  return props.type === TemplateContentType.IMAGE && file.value?.fileContent;
});
const maxSize = computed(() => {
  return Number(convertTo(formatContent[props.type].sizeLimit, 'MB'));
});

watch(
  () => file.value,
  () => {
    emit("valueChanged", file.value);
  }
);

const onError = (error: InputFileError) => {
	isError.value = error.errorType;
}
// дополнительная обратотка так как ui-kit компонент не возвращает данные о файле если он не прошол валидацию
const handleFileChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const rawFile = input.files?.[0];

    if (rawFile) {
      fileSize.value = Number(convertTo(rawFile.size, 'MB'));
    }
};
</script>
<template>
  <PInputFile
    v-model="file"
    class="file-input"
    :accept="formatContent[type].formats"
    :multiple="false"
    :readonly="false"
    :upload-limit="formatContent[type].sizeLimit"
    @change:error="onError"
    @change="handleFileChange"
  >
    <template #activator="{ selectFile }">
      <div class="file-input">
        <div class="input" @click="selectFile">
          <p class="title">{{ props.title }}</p>
          <p class="description">
            {{ $t(`broadcast.uploadFile.${type.toLowerCase()}.description`) }}
          </p>
        </div>

        <div v-if="isError" class="file-input-error" :class="{ 'c-[#E46060]': isError }">
          <span v-if="isError === Errors.fileSizeLimitExceeded">
            {{ $t(`broadcast.uploadFile.${props.type.toLowerCase()}.maxSizeError`, { maxSize, fileSize }) }}
          </span>
          <span v-if="isError === Errors.fileTypeInvalid && file">
            {{ $t(`broadcast.uploadFile.errors.fileInvalidFormat.description`) }}
          </span>
        </div>
        <div class="preview-wrapper">
          <div
            v-if="file"
            :class="[
              'preview',
              { image: withPreview },
              { empty: !withPreview },
            ]"
            @click="file = null; isError = null; fileSize = null"
          >
            <img v-if="withPreview" :src="file?.fileContent" alt="" width="100%"/>
            <TrashIcon class="trash-icon color-[#FFFFFF]" />
          </div>

          <div v-else class="empty" @click="(selectFile(), isError = null, fileSize = null)">
            <component
              :is="formatContent[props.type].icon"
            />
          </div>
        </div>
      </div>
    </template>
  </PInputFile>
</template>
<style lang="scss" scoped>
.file-input {
  border-radius: 8px;
  border: 1px dashed var(--common-rain);
  background: var(--basic-white);
  padding: 12px 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  justify-content: space-between;

  .input {
    flex: 1;

    .title {
      color: var(--common-black);
      font-size: 16px;
      font-weight: 600;
      line-height: 150%;
    }

    .not-picked {
      color: var(--additional-error);
      font-size: 12px;
      line-height: 133.3%;
    }

    .description {
      color: var(--common-asphalt);
      font-size: 12px;
      line-height: 133.3%;
    }
  }
}

.preview-wrapper {
  .preview {
    width: 56px;
    height: 56px;
    border-radius: 56px;
    background: var(--additional-pale-blue);
    position: relative;
    .trash-icon {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      margin: auto;
    }
    &.empty {
      background: rgba(140,160,172,255);
    }
  }
  .image {
    overflow: hidden;
    background: transparent;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .empty {
    display: flex;
    padding: 18px;
    border-radius: 50px;
    background: var(--additional-pale-blue);
  }
}
</style>
